import { useState } from 'react';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import UseInput from '../../hooks/useInput';
import { useContactForm } from '../../hooks/useContactForm';
import { validateContactEmail, validateNotEmpty } from './ValidationLogic';
import Swal from 'sweetalert2';
import SharedButton from '@/global/Button/Button';
import Loader from '@/components/Common/Loader';
import BackgroundPages from '@/global/Background/BackgroundPages';
import useText from '@/hooks/useText';

export default function SupportContent() {
  const [isPolicyAccepted, setIsPolicyAccepted] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const support = useText('home.footer.support');

  const { isSubmitting, emailSentMessage, formError, setFormError, submitMessage, resetForm } =
    useContactForm();

  const {
    value: name,
    isInputValid: isNameValid,
    isInputInvalid: isNameInvalid,
    inputClassName: nameClassName,
    valueChangeHandler: nameChangeHandler,
    valueBlurHandler: nameBlurHandler,
    resetInput: resetNameInput,
  } = UseInput(validateNotEmpty);

  const {
    value: email,
    isInputValid: isEmailValid,
    isInputInvalid: isEmailInvalid,
    inputClassName: emailClassName,
    valueChangeHandler: emailChangeHandler,
    valueBlurHandler: emailBlurHandler,
    resetInput: resetEmailInput,
  } = UseInput(validateContactEmail);

  const {
    value: company,
    isInputValid: isCompanyValid,
    isInputInvalid: isCompanyInvalid,
    inputClassName: companyClassName,
    valueChangeHandler: companyChangeHandler,
    valueBlurHandler: companyBlurHandler,
    resetInput: resetCompanyInput,
  } = UseInput(validateNotEmpty);

  const {
    value: message,
    isInputValid: isMessageValid,
    isInputInvalid: isMessageInvalid,
    inputClassName: messageClassName,
    valueChangeHandler: messageChangeHandler,
    valueBlurHandler: messageBlurHandler,
    resetInput: resetMessageInput,
  } = UseInput(validateNotEmpty);

  function resetFormHandler() {
    resetNameInput();
    resetEmailInput();
    resetCompanyInput();
    resetMessageInput();
    setIsPolicyAccepted(false);
    resetForm();
    setSelectedSubject('');
    setIsPopupVisible(false);
  }

  const handleGoToPrivacyPolicy = () => {
    window.open('/privacy-policy', '_blank');
  };

  function checkFormValidity() {
    const inputsValidity = [isNameValid, isEmailValid, isCompanyValid, isMessageValid];
    return (
      inputsValidity.every(isInputValid => isInputValid) &&
      selectedSubject !== 'I would like to apply for an academic license'
    );
  }

  const handleDisableSubmitButton = () => {
    return (
      !isPolicyAccepted ||
      selectedSubject === 'I would like to apply for an academic license' ||
      !checkFormValidity()
    );
  };

  async function submitMessageHandler(event) {
    event.preventDefault();
    const messageInfo = {
      name: name,
      email: email,
      organization: company,
      message: message,
      subject: selectedSubject,
    };
    if (checkFormValidity()) {
      const success = await submitMessage(messageInfo);
      if (success) {
        Swal.fire({
          title: 'Form sent successfully',
          text: 'We will get back to you shortly.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3B82F6',
        });
        resetFormHandler();
      }
    } else {
      setFormError('Validation failed. Check your inputs and try again');
    }
  }

  const debouncedSubmitHandler = debounce(
    event => {
      event.preventDefault();
      submitMessageHandler(event);
    },
    500,
    { leading: true, trailing: false },
  );

  function onChangePolicyHandler() {
    setIsPolicyAccepted(prevState => !prevState);
  }

  function handleSubjectChange(e) {
    const value = e.target.value;
    setSelectedSubject(value);
    if (value === 'I would like to apply for an academic license') {
      setIsPopupVisible(true);
    } else {
      setIsPopupVisible(false);
    }
  }

  return (
    <>
      <BackgroundPages />
      <section>
        {isSubmitting && <Loader />}
        <link rel="canonical" href="/Support" />
        <div className="apply-trial-content md:mt-24 mt-20">
          <div className="home-hero-content-support">
            <div className="home-hero-section">
              <h1 className="home-hero-title">{support}</h1>
              <p className="home-hero-subtitle">
                If you need assistance with licensing options, upgrading your account, or have other
                inquiries, please feel free to fill out the form below.
              </p>
              <div className="row text-left">
                <div className="form-apply form-apply-support w-full max-w-lg rounded-2xl ">
                  <form>
                    {/* Name */}
                    <div data-validate="Name is required">
                      <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                        Name *
                      </label>
                      <input
                        className="form-input"
                        type="text"
                        name="name"
                        placeholder="Name..."
                        onChange={nameChangeHandler}
                        onBlur={nameBlurHandler}
                        value={name}
                      />
                      <div>{isNameInvalid && <p className="form-error mt-2">Please enter your name</p>}</div>
                    </div>
                    {/* Email */}
                    <div data-validate="Valid email is required: ex@abc.xyz" className="mt-4">
                      <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                        Email *
                      </label>
                      <input
                        className="form-input"
                        type="text"
                        name="email"
                        placeholder="Email address..."
                        onChange={emailChangeHandler}
                        onBlur={emailBlurHandler}
                        value={email}
                      />
                      <div>
                        {isEmailInvalid && (
                          <p className="form-error mt-2">Please enter a valid email: name@example.com</p>
                        )}
                      </div>
                    </div>
                    {/* Company */}
                    <div data-validate="Name is required" className="mt-4">
                      <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                        Company *
                      </label>
                      <input
                        className="form-input"
                        type="text"
                        name="company"
                        placeholder="Company..."
                        onChange={companyChangeHandler}
                        onBlur={companyBlurHandler}
                        value={company}
                      />
                      <span className="focus-input100" />
                      <div>
                        {isCompanyInvalid && (
                          <p className="form-error mt-2">Please enter your company&apos;s name</p>
                        )}
                      </div>
                    </div>
                    {/* Subject */}
                    <div className="mt-4">
                      <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                        Subject *
                      </label>
                      <select
                        className="form-input"
                        name="subject"
                        onChange={handleSubjectChange}
                        value={selectedSubject}
                      >
                        <option value="">...</option>
                        <option value="I would like to apply for an academic license">
                          I would like to apply for an academic license
                        </option>
                        <option value="Request pricing">Request pricing</option>
                        <option value="I would like more information about the product or license">
                          I would like more information about the product or license
                        </option>
                        {/* <option value="I would like to provide feedback">
                        I would like to provide feedback
                      </option> */}
                        <option value="I would like to contact the support team">
                          I would like to contact the support team
                        </option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    {/* Message */}
                    <div data-validate="Message is required" className="mt-4 mb-2">
                      <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                        Message *
                      </label>
                      <textarea
                        className="form-input"
                        name="message"
                        placeholder="Questions/Comments..."
                        onChange={messageChangeHandler}
                        onBlur={messageBlurHandler}
                        value={message}
                      />
                      <span className="focus-input100" />
                      {isMessageInvalid && (
                        <p className="form-error mt-2">Please enter your question / comments</p>
                      )}
                    </div>
                    {/* Checkboxes */}
                    <div className="form-check text-left mt-2 ml-2 mb-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        name="academicLicenseCheckbox"
                        checked={isPolicyAccepted}
                        onChange={onChangePolicyHandler}
                      />
                      <label className="text-xs">
                        I have read and accept{' '}
                        <button
                          type="button"
                          onClick={handleGoToPrivacyPolicy}
                          className="text-sm font-semibold"
                        >
                          DISGENET&apos;s privacy policy
                        </button>
                      </label>
                    </div>

                    <div>
                      {formError && <p className="form-error">{formError}</p>}
                      {emailSentMessage && <p className="form-sucess">{emailSentMessage}</p>}
                    </div>
                    <div className="container-contact100-form-btn">
                      <div className="wrap-contact100-form-btn">
                        <SharedButton
                          variant="primary"
                          size="md"
                          disabled={handleDisableSubmitButton()}
                          title={
                            selectedSubject ? 'apply for an academic license on the academic apply page.' : ''
                          }
                          onClick={debouncedSubmitHandler}
                        >
                          Send
                        </SharedButton>
                      </div>
                    </div>
                  </form>
                </div>
                <div className="col-sm-12 col-md-12 col-lg-6 responsive-map">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2993.3454629823714!2d2.162852784754208!3d41.38830339729827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4a2f2528cd1a7%3A0xcab870e40ece5da8!2sRambla de Catalunya%2C 14%2C 08007 Barcelona!5e0!3m2!1ses-419!2ses!4v1699352156342!5m2!1ses-419!2ses"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
            <div className="my-5 text-center">
              <p className='text-gray-600'>You can also find answers to common questions in our Help Center.</p>
              <SharedButton
                variant="gradient"
                size="sm"
                onClick={() =>
                  window.open('https://disgenet.freshdesk.com/support/home', '_blank', 'noopener,noreferrer')
                }
              >
                Visit Help Center
              </SharedButton>
            </div>
          </div>
        </div>

        {isPopupVisible && (
          <div className="popup-contact">
            <div className="popup-inner-contact">
              <h3 className="p-2">IMPORTANT</h3>
              <h6 className="pb-2">
                To apply for an academic license, please go to the{' '}
                <Link to="/academic-apply" onClick={() => setIsPopupVisible(false)}>
                  academic apply page
                </Link>
                .
              </h6>
              <button onClick={() => setIsPopupVisible(false)}>Close</button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
